import { Content, ContentPublish, Classic, Comment } from "@/types/content";
import { ApiFullTagDescription, LIST_TAG, api } from "..";
import { buildQueryString } from "./utils";
import { ResultOk } from "@/types/resultok";

export const contentApi = api.injectEndpoints({
  endpoints: (build) => ({
    //获取内容分页列表--个人中心，创作者中心->我的AI创作
    getPersonalContents: build.query<Content[], { page: number; per_page: number; classicName: string; userId: string }>({
      query: ({ page, per_page, classicName, userId }) => `/contents/personal${buildQueryString({ page, per_page, classicName, userId })}`,
      transformErrorResponse: (baseQueryReturnValue, meta, arg) => baseQueryReturnValue.data,
      providesTags: (result, error, arg) => {
        const tags: ApiFullTagDescription[] = [{ id: "Content", type: LIST_TAG }];
        if (result) {
          // and individual tags for each content
          tags.push(...result.map(({ id }) => ({ type: "Content" as const, id: id })));
        }
        return tags;
      },
    }),
    //获取内容分页列表--广场
    getContents: build.query<Content[], { page: number; per_page: number; classicName: string; userId: string; origin: number }>({
      query: ({ page, per_page, classicName, userId, origin }) => `/contents/classicname${buildQueryString({ page, per_page, classicName, userId, origin })}`,
      transformErrorResponse: (baseQueryReturnValue, meta, arg) => baseQueryReturnValue.data,
      providesTags: (result, error, arg) => {
        const tags: ApiFullTagDescription[] = [{ id: "Content", type: LIST_TAG }];
        if (result) {
          // and individual tags for each content
          tags.push(...result.map(({ id }) => ({ type: "Content" as const, id: id })));
        }
        return tags;
      },
    }),
    //创建内容
    createContent:build.mutation<Content,Content>({
      query: (content) => ({
        url: `/contents`,
        method: 'POST',
        body:content
      }),
      invalidatesTags: [{ id: 'Content', type: LIST_TAG }],
    }),
    //更新内容
    updateContent:build.mutation<Content,ContentPublish>({
      query: (ContentPublish) => ({
        url: `/contents`,
        method: 'PUT',
        body: ContentPublish,
      }),
      transformErrorResponse: (baseQueryReturnValue, meta, arg) => baseQueryReturnValue.data,
      invalidatesTags: (result, error, arg) => [{ type: 'Content', id: arg.id }]
    }),
    //删除内容
    deleteContent:build.mutation<ResultOk,string>({
      query: (content_id) => ({
        url: `/contents/${content_id}`,
        method: 'DELETE'
      }),
      transformErrorResponse: (baseQueryReturnValue, meta, arg) => baseQueryReturnValue.data,
      invalidatesTags: (result, error, arg) => [{ type: 'Content', id: arg }],
    }),
    //获取内容详情
    getContent:build.query<ContentPublish,string>({
      query:(content_id)=>({
        url: `/contents/${content_id}/detail`,
        method: 'GET'
      }),
      transformErrorResponse: (baseQueryReturnValue, meta, arg) => baseQueryReturnValue.data,
      providesTags: (result, error, id) => [{ type: 'Content', id }],
    }),
    //获取分类
    getClassics: build.query<Classic[],{}>({
      query:()=>({
        url: `/contents/classic`,
        method: 'GET'
      }),
      transformErrorResponse: (baseQueryReturnValue, meta, arg) => baseQueryReturnValue.data,
    }),
    //点赞内容
    likeContent: build.mutation<ResultOk,{user_id:string,content_id:string,status:boolean}>({
      query:(body)=>({
        url: `/contents/like`,
        method: 'POST',
        body
      }),
      transformErrorResponse: (baseQueryReturnValue, meta, arg) => baseQueryReturnValue.data,
      invalidatesTags: (result, error, arg) => [{ type: 'Content', id: arg.content_id }],
    }),
    //收藏内容
    collectContent: build.mutation<ResultOk,{user_id:string,content_id:string,status:boolean}>({
      query:(body)=>({
        url: `/contents/collect`,
        method: 'POST',
        body
      }),
      transformErrorResponse: (baseQueryReturnValue, meta, arg) => baseQueryReturnValue.data,
      invalidatesTags: (result, error, arg) => [{ type: 'Content', id: arg.content_id }],
    }),
    //评论内容
    commentContent: build.mutation<ResultOk,{user_id:string,content_id:string,comment_detail:string,root_id:string}>({
      query:(body)=>({
        url: `/contents/comment`,
        method: 'POST',
        body
      }),
      transformErrorResponse: (baseQueryReturnValue, meta, arg) => baseQueryReturnValue.data,
      invalidatesTags: (result, error, arg) => [{ type: 'Comment', id: arg.content_id },{ type: 'Content', id: arg.content_id }],
    }),
    //点赞内容列表
    likeContents: build.query<Content[],string>({
      query:(userId)=>({
        url: `/contents/like/${userId}`,
        method: 'GET',
      }),
      transformErrorResponse: (baseQueryReturnValue, meta, arg) => baseQueryReturnValue.data,
      providesTags: (result, error, arg) => {
        const tags: ApiFullTagDescription[] = [{ id: "Content", type: LIST_TAG }];
        if (result) {
          // and individual tags for each content
          tags.push(...result.map(({ id }) => ({ type: "Content" as const, id: id })));
        }
        return tags;
      },
    }),
    //收藏内容列表
    collectContents: build.query<Content[],string>({
      query:(userId)=>({
        url: `/contents/collect/${userId}`,
        method: 'GET',
      }),
      transformErrorResponse: (baseQueryReturnValue, meta, arg) => baseQueryReturnValue.data,
      providesTags: (result, error, arg) => {
        const tags: ApiFullTagDescription[] = [{ id: "Content", type: LIST_TAG }];
        if (result) {
          // and individual tags for each content
          tags.push(...result.map(({ id }) => ({ type: "Content" as const, id: id })));
        }
        return tags;
      },
    }),
    //评论列表
    getComments: build.query<Comment[],string>({
      query:(contentId)=>({
        url: `/contents/${contentId}/comments`,
        method: 'GET',
      }),
      transformErrorResponse: (baseQueryReturnValue, meta, arg) => baseQueryReturnValue.data,
      providesTags: (result, error, contentId) => {
        const tags: ApiFullTagDescription[] = [{ type: 'Comment', id: contentId },{ id: "Comment", type: LIST_TAG }];
        if (result) {
          // and individual tags for each content
          tags.push(...result.map(({ id }) => ({ type: "Comment" as const, id: id })));
        }
        return tags;
      },
    }),
    //历史列表
    getHistory: build.query<Content[],string>({
      query:(userId)=>({
        url: `/contents/history/${userId}`,
        method: 'GET',
      }),
      transformErrorResponse: (baseQueryReturnValue, meta, arg) => baseQueryReturnValue.data
    }),
  }),
});

export const {
  useCreateContentMutation,
  useDeleteContentMutation,
  useUpdateContentMutation,
  useGetContentsQuery,
  useLazyGetContentsQuery,
  useLazyGetPersonalContentsQuery,
  useGetContentQuery, 
  useGetClassicsQuery,
  useLazyGetClassicsQuery,
  useLikeContentMutation,
  useCollectContentMutation,
  useLazyCollectContentsQuery,
  useCommentContentMutation,
  useGetCommentsQuery,
  useLazyLikeContentsQuery,
  useLazyGetHistoryQuery,
  useLazyGetContentQuery
} = contentApi

